import type { Options } from 'qr-code-styling'

/**
 * Branded QR codes — the single place a tenant logo gets drawn into a QR.
 *
 * Before this module there were five independent `qr-code-styling` / `qrcode`
 * configurations with divergent error-correction levels, and the enrolment tab
 * built its preview and its download from two separate literals that could
 * silently drift. Everything now funnels through `buildBrandedQrOptions`, which
 * is also the only place allowed to decide error correction.
 *
 * ── Why error correction is the whole story ──────────────────────────────
 * A logo in the middle of a QR works only because QR carries Reed–Solomon
 * error correction: some fraction of the modules can be destroyed and the
 * code still decodes. That fraction is set by the level:
 *
 *     L 7%   M 15%   Q 25%   H 30%
 *
 * so a logo is a withdrawal from a fixed damage budget. Spend all of it on
 * artwork and there is nothing left for the real-world damage the budget
 * exists to absorb — a scuffed table tent, a thermal head with a cold spot,
 * a cheap phone camera in a dim café. Whenever we draw a logo we therefore
 * force level H and spend only part of the enlarged budget.
 *
 * ── The units trap in `imageOptions.imageSize` ───────────────────────────
 * `imageSize` is NOT a fraction of the QR's area. qr-code-styling computes
 *
 *     maxHiddenDots = imageSize * errorCorrectionPercent[level] * count^2
 *
 * so it is a fraction *of the error-correction budget*, already normalised by
 * level. The library default of 0.4 means "consume 40% of the budget". At H
 * that hides ~12% of modules; at M it would hide ~6%. Read as an area
 * fraction it looks far more aggressive than it is, and clamping it as though
 * it were an area fraction yields a logo too small to see.
 *
 * QR_LOGO_SCALE_MAX = 0.6 therefore means "never spend more than 60% of the
 * correction budget on artwork", i.e. ~18% of modules hidden at H, with 40%
 * of the budget left for damage. That is the safety property that matters.
 */

/** Fraction of the error-correction budget spent on the logo. See above. */
export const QR_LOGO_SCALE_MIN = 0.25
export const QR_LOGO_SCALE_MAX = 0.6
export const QR_LOGO_SCALE_DEFAULT = 0.45

/**
 * Below this rendered size a logo is dropped and a plain QR returned. Small
 * codes have few device pixels per module, so the knockout pad and the logo's
 * own edges start landing mid-module where a scanner cannot recover them.
 * Thermal safety is handled separately by `toMonochromeDataUrl` — this gate is
 * about resolution, not about ink.
 */
export const QR_LOGO_MIN_RENDER_PX = 110

export type BrandedQrShapeStyle = {
  dark_color: string
  light_color: string
  /**
   * Optional accent for the three finder-pattern centres. The enrolment QR
   * uses the tenant's accent colour here. Defaults to dark_color.
   */
  corner_dot_color?: string
  margin_modules: number
  dots_type: NonNullable<NonNullable<Options['dotsOptions']>['type']>
  corner_square_type: NonNullable<NonNullable<Options['cornersSquareOptions']>['type']>
  corner_dot_type: NonNullable<NonNullable<Options['cornersDotOptions']>['type']>
  error_correction: NonNullable<NonNullable<Options['qrOptions']>['errorCorrectionLevel']>
}

export type BuildBrandedQrArgs = {
  data: string
  size: number
  style: BrandedQrShapeStyle
  /**
   * Logo as a data: URL. Must be a data URL rather than a remote URL — see
   * `loadBrandLogo`. Null/undefined yields a plain QR.
   */
  logoDataUrl?: string | null
  /**
   * Caller's intent. Even when true, the logo is dropped if there is no image
   * or the code is too small to carry one.
   */
  logoEnabled?: boolean
  /** Fraction of the correction budget to spend. Clamped to the range above. */
  logoScale?: number
}

export function clampLogoScale(raw: number | undefined | null): number {
  if (typeof raw !== 'number' || !Number.isFinite(raw)) return QR_LOGO_SCALE_DEFAULT
  return Math.min(QR_LOGO_SCALE_MAX, Math.max(QR_LOGO_SCALE_MIN, raw))
}

/**
 * True when a logo will actually be drawn. Exported so UI can explain itself
 * ("too small for a logo at this size") instead of silently ignoring a toggle.
 */
export function willDrawLogo(args: BuildBrandedQrArgs): boolean {
  return Boolean(args.logoEnabled && args.logoDataUrl && args.size >= QR_LOGO_MIN_RENDER_PX)
}

/**
 * The one place error correction and image options are decided.
 *
 * Note the ECC override: a caller's configured level is honoured for plain
 * QRs, but a logo always forces H. The two are not independent settings —
 * drawing artwork over a level-M code is how you get a QR that scans on the
 * designer's iPhone and fails on half the customers' phones.
 */
export function buildBrandedQrOptions(args: BuildBrandedQrArgs): Options {
  const { data, size, style } = args
  const drawLogo = willDrawLogo(args)

  const base: Options = {
    width: size,
    height: size,
    type: 'canvas',
    data,
    margin: style.margin_modules,
    // Logo ⇒ H, always. Never the caller's level.
    qrOptions: { errorCorrectionLevel: drawLogo ? 'H' : style.error_correction },
    dotsOptions: { color: style.dark_color, type: style.dots_type },
    cornersSquareOptions: { color: style.dark_color, type: style.corner_square_type },
    cornersDotOptions: {
      color: style.corner_dot_color || style.dark_color,
      type: style.corner_dot_type,
    },
    backgroundOptions: { color: style.light_color },
  }

  if (!drawLogo) return base

  return {
    ...base,
    image: args.logoDataUrl as string,
    imageOptions: {
      // Clear the modules under the logo rather than letting artwork sit on
      // top of them. A module that is absent is a clean erasure the decoder
      // corrects; a module half-covered by a logo edge can be read as the
      // wrong colour, which is worse than missing.
      hideBackgroundDots: true,
      imageSize: clampLogoScale(args.logoScale),
      // Quiet white pad (px) so the logo never visually merges into the
      // surrounding modules — helps humans and scanners find the boundary.
      margin: 4,
    },
  }
}
